/**
 * A refusal from `/api/profile-links/<op>` with the route's own code — `not_implemented`, `not_found`,
 * `cross_origin`, `invalid_patch`, … Its own import-free module so the fixtures and the pure screen logic can
 * name it without dragging `lib/api-client` into a unit test.
 */
export class ProfileLinksApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
  ) {
    super(`profile-links: ${code} (${status})`);
    this.name = 'ProfileLinksApiError';
  }
}

/**
 * True for "profile links are not available to this caller", which the screens render as their own state
 * rather than as an error or an empty account: the backend not deployed (`501 not_implemented`), or the
 * service refusing the owner (`403 forbidden` — outside `symphonyProfileLinks`, or the per-method
 * permission has no Dev Center grant yet). iOS and Android read the same two codes the same way.
 */
export function isNotImplemented(err: unknown): boolean {
  return err instanceof ProfileLinksApiError && (err.code === 'not_implemented' || (err.status === 403 && err.code === 'forbidden'));
}

/**
 * The reasons a write the service DECLINED carries (`ProfileLinkRefusal.Reason`), as the bridge relays them:
 * a 422 whose error code is the reason, lower case (ai-scheduling-assistant-server `profileLinksWire.ts`).
 * Each has its own copy, `profileLinks.refusal.<reason>` — the same key names iOS and Android read.
 */
export const PROFILE_LINK_REFUSALS = ['content_policy', 'not_eligible', 'handle_required', 'invalid'] as const;
export type ProfileLinkRefusalReason = (typeof PROFILE_LINK_REFUSALS)[number];

/** Every bridge code the owner screens read off a failed call; `/api/profile-links/<op>` passes on only these. */
export const PROFILE_LINK_ERROR_CODES: readonly string[] = ['not_implemented', 'forbidden', ...PROFILE_LINK_REFUSALS];

/**
 * The copy key an owner screen shows for a failed call: "not available" for the two not-implemented
 * answers, the refusal's own sentence for a declined write, and the generic "Couldn't save" for anything
 * else — an unknown reason included, so a reason the service adds later still reads as an error.
 */
export function profileLinkErrorKey(err: unknown): string {
  if (isNotImplemented(err)) return 'profileLinks.unavailable.body';
  if (err instanceof ProfileLinksApiError && err.status === 422 && (PROFILE_LINK_REFUSALS as readonly string[]).includes(err.code)) {
    return `profileLinks.refusal.${err.code}`;
  }
  return 'profileLinks.error.saveFailed';
}
